/**
 * V250: 증명서 발급 페이지 (직원용)
 *
 * - 증명서 신청 (현재 재직증명서만)
 * - 내 신청 목록 조회
 * - 승인된 증명서 PDF 다운로드
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Download, X, AlertCircle, Loader, Eye } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useModalOverlayClose } from '../../shared/hooks/useModalOverlayClose';
import { useCertificates, CertificateRequest } from '../../shared/hooks/useCertificates';
import { useAuthContext } from '../../shared/hooks/useAuthContext';

const STATUS_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  pending: { label: '대기중', color: '#d97706', bg: '#fef3c7' },
  approved: { label: '승인', color: '#059669', bg: '#d1fae5' },
  rejected: { label: '반려', color: '#dc2626', bg: '#fee2e2' },
  pdf_failed: { label: 'PDF 오류', color: '#dc2626', bg: '#fee2e2' },
};

const TYPE_LABELS: Record<string, string> = {
  employment: '재직증명서',
  career: '경력증명서',
  salary: '급여증명서',
};

export function MyCertificatesPage() {
  const authContext = useAuthContext();
  const navigate = useNavigate();

  const {
    isLoading,
    error,
    createRequest,
    listMyRequests,
    getDownloadUrl,
  } = useCertificates();

  const [requests, setRequests] = useState<CertificateRequest[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formPurpose, setFormPurpose] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [downloading, setDownloading] = useState<string | null>(null);
  const [rejectReasonModal, setRejectReasonModal] = useState<CertificateRequest | null>(null);
  const [pageError, setPageError] = useState<string | null>(null);

  // V273: 모달 오버레이 드래그 오작동 방지
  const formOverlayProps = useModalOverlayClose(() => setShowForm(false));
  const rejectReasonOverlayProps = useModalOverlayClose(() => setRejectReasonModal(null));

  const fetchRequests = useCallback(async () => {
    try {
      setPageError(null);
      const items = await listMyRequests();
      setRequests(items);
    } catch {
      setPageError('증명서 목록을 불러오지 못했습니다.');
    }
  }, [listMyRequests]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  const handleSubmit = async () => {
    if (!formPurpose.trim()) return;
    try {
      setSubmitting(true);
      await createRequest({
        type: 'employment',
        purpose: formPurpose.trim(),
      });
      setShowForm(false);
      setFormPurpose('');
      await fetchRequests();
    } catch {
      // error is set by hook
    } finally {
      setSubmitting(false);
    }
  };

  const handleDownload = async (req: CertificateRequest) => {
    try {
      setDownloading(req.id);
      const result = await getDownloadUrl(req.id);
      // 새 탭에서 다운로드
      const a = document.createElement('a');
      a.href = result.downloadUrl;
      a.target = '_blank';
      a.download = result.fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch {
      // error is set by hook
    } finally {
      setDownloading(null);
    }
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    } catch {
      return '-';
    }
  };

  // V257: 파견직 증명서 발급 차단 — URL 직접 접근 방어
  if (authContext.isLoaded && authContext.currentEmploymentTypeCode === 'DISPATCH') {
    return (
      <div style={{ padding: '24px', maxWidth: 960, margin: '0 auto' }}>
        <div style={{
          padding: '40px 24px', textAlign: 'center', backgroundColor: '#fef2f2',
          borderRadius: 12, border: '1px solid #fecaca',
        }}>
          <AlertCircle size={48} color="#dc2626" style={{ marginBottom: 16 }} />
          <h2 style={{ fontSize: 20, fontWeight: 700, color: '#991b1b', margin: '0 0 8px 0' }}>
            접근 불가
          </h2>
          <p style={{ fontSize: 15, color: '#dc2626', margin: '0 0 24px 0' }}>
            파견직은 증명서 발급이 불가합니다.
          </p>
          <button
            onClick={() => navigate('/work/dashboard')}
            style={{
              padding: '8px 24px', backgroundColor: '#3b82f6', color: '#fff',
              border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
            }}
          >
            대시보드로 이동
          </button>
        </div>
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', maxWidth: 960, margin: '0 auto' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>증명서 발급</h1>
        <button
          onClick={() => setShowForm(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', backgroundColor: '#3b82f6', color: '#fff',
            border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600, cursor: 'pointer',
          }}
        >
          <Plus size={16} /> 신규 신청
        </button>
      </div>

      {/* 에러 */}
      {(pageError || error) && (
        <div style={{
          padding: '12px 16px', marginBottom: 16, backgroundColor: '#fee2e2',
          borderRadius: 8, color: '#dc2626', display: 'flex', alignItems: 'center', gap: 8,
        }}>
          <AlertCircle size={16} /> {pageError || error}
        </div>
      )}

      {/* 목록 */}
      <div style={{ backgroundColor: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>종류</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>용도</th>
              <th style={{ padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#374151' }}>신청일</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>상태</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>발급번호</th>
              <th style={{ padding: '12px 16px', textAlign: 'center', fontWeight: 600, color: '#374151' }}>액션</th>
            </tr>
          </thead>
          <tbody>
            {isLoading && requests.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                  <Loader size={20} style={{ animation: 'spin 1s linear infinite', display: 'inline-block' }} /> 로딩 중...
                </td>
              </tr>
            ) : requests.length === 0 ? (
              <tr>
                <td colSpan={6} style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>
                  신청 내역이 없습니다.
                </td>
              </tr>
            ) : (
              requests.map(req => {
                const status = STATUS_LABELS[req.status] || { label: req.status, color: '#6b7280', bg: '#f3f4f6' };
                return (
                  <tr key={req.id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '12px 16px' }}>{TYPE_LABELS[req.type] || req.type}</td>
                    <td style={{ padding: '12px 16px' }}>{req.purpose}</td>
                    <td style={{ padding: '12px 16px' }}>{formatDate(req.requestedAt)}</td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      <span style={{
                        padding: '2px 10px', borderRadius: 12, fontSize: 12, fontWeight: 600,
                        color: status.color, backgroundColor: status.bg,
                      }}>
                        {status.label}
                      </span>
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center', fontSize: 12, color: '#6b7280' }}>
                      {req.issueNumber || '-'}
                    </td>
                    <td style={{ padding: '12px 16px', textAlign: 'center' }}>
                      {req.status === 'approved' && (
                        <button
                          onClick={() => handleDownload(req)}
                          disabled={downloading === req.id}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '4px 12px', backgroundColor: '#059669', color: '#fff',
                            border: 'none', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                          }}
                        >
                          {downloading === req.id ? <Loader size={12} /> : <Download size={12} />} 다운로드
                        </button>
                      )}
                      {req.status === 'rejected' && (
                        <button
                          onClick={() => setRejectReasonModal(req)}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 4,
                            padding: '4px 12px', backgroundColor: '#f3f4f6', color: '#374151',
                            border: '1px solid #d1d5db', borderRadius: 4, fontSize: 12, cursor: 'pointer',
                          }}
                        >
                          <Eye size={12} /> 사유보기
                        </button>
                      )}
                      {req.status === 'pending' && (
                        <span style={{ color: '#9ca3af', fontSize: 12 }}>처리 대기</span>
                      )}
                      {req.status === 'pdf_failed' && (
                        <span style={{ color: '#dc2626', fontSize: 12 }}>PDF 오류</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* 신청 모달 */}
      {showForm && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} {...formOverlayProps}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 24, width: 420,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>증명서 신청</h2>
              <button onClick={() => setShowForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>종류</label>
              <select
                disabled
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                  fontSize: 14, backgroundColor: '#f9fafb', color: '#374151',
                }}
              >
                <option value="employment">재직증명서</option>
              </select>
              <p style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>현재는 재직증명서만 신청 가능합니다.</p>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ display: 'block', fontSize: 14, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                용도 <span style={{ color: '#dc2626' }}>*</span>
              </label>
              <input
                type="text"
                value={formPurpose}
                onChange={e => setFormPurpose(e.target.value)}
                placeholder="예: 비자신청, 은행제출, 기관제출"
                style={{
                  width: '100%', padding: '8px 12px', borderRadius: 6, border: '1px solid #d1d5db',
                  fontSize: 14, boxSizing: 'border-box',
                }}
              />
            </div>

            {error && (
              <div style={{
                padding: '8px 12px', marginBottom: 16, backgroundColor: '#fee2e2',
                borderRadius: 6, color: '#dc2626', fontSize: 13,
              }}>
                {error}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowForm(false)}
                style={{
                  padding: '8px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, cursor: 'pointer',
                }}
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !formPurpose.trim()}
                style={{
                  padding: '8px 20px', backgroundColor: submitting || !formPurpose.trim() ? '#93c5fd' : '#3b82f6',
                  color: '#fff', border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 600,
                  cursor: submitting || !formPurpose.trim() ? 'not-allowed' : 'pointer',
                }}
              >
                {submitting ? '신청 중...' : '신청'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* 반려 사유 모달 */}
      {rejectReasonModal && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.4)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000,
        }} {...rejectReasonOverlayProps}>
          <div style={{
            backgroundColor: '#fff', borderRadius: 12, padding: 24, width: 400,
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ fontSize: 18, fontWeight: 700, margin: 0 }}>반려 사유</h2>
              <button onClick={() => setRejectReasonModal(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}>
                <X size={20} color="#6b7280" />
              </button>
            </div>
            <div style={{
              padding: 16, backgroundColor: '#f9fafb', borderRadius: 8,
              fontSize: 14, color: '#374151', lineHeight: 1.6,
            }}>
              {rejectReasonModal.rejectReason || '사유 없음'}
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 16 }}>
              <button
                onClick={() => setRejectReasonModal(null)}
                style={{
                  padding: '8px 20px', backgroundColor: '#f3f4f6', color: '#374151',
                  border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, cursor: 'pointer',
                }}
              >
                닫기
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
